import "./PatientAnthropometry.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

import ChevronRightIcon from "../../assets/imgs/chevron-right-icon.svg";
import DeleteIcon from "../../assets/imgs/delete-icon.svg";
import HistoryArrowDownIcon from "../../assets/imgs/history-arrow-down.png";
import HistoryArrowUpIcon from "../../assets/imgs/history-arrow-up.png";
import DefaultButton from "../../components/DefaultButton/DefaultButton";
import DefaultModal from "../../components/DefaultModal/DefaultModal";
import NumericPagination from "../../components/NumericPagination/NumericPagination";
import NotificationBar from "../../components/NotificationBar/NotificationBar";
import StatusTag from "../../components/StatusTag/StatusTag";
import loginService from "../../services/login-service";
import measurementService from "../../services/measurement-service";
import userService from "../../services/user-service";
import { GlobalContext } from "../GlobalContext";
import {
  buildAutomaticCalculationItems,
  buildCircumferenceItems,
  buildMinimalHistoryPayload,
  formatVisitDateLabel,
  measurementHasCircumference,
  toApiDate,
  todayDisplayDate,
} from "./circumference-utils";
import { skinfoldEditMeasurementCacheKey, measurementHasSkinfolds } from "./skinfold-utils";
import { buildSkinfoldHubItems } from "./skinfold-bodyfat";
import { buildCompositionProgressChartModel } from "./composition-progress-chart";
import { isDraftMeasurement } from "./draft-session-utils";
import DraftSessionActions from "./DraftSessionActions";
import {
  buildSavePayloadFromPending,
  clearPendingEdit,
  hasPendingEdit,
  mergePendingIntoMeasurement,
  readPendingEdit,
} from "./edit-pending-utils";

const HISTORY_PAGE_SIZE = 5;

ChartJS.register(
  LinearScale,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  Filler
);

/** Newest first: visit_date desc, then id desc (same-day records). */
const sortMeasurementsNewestFirst = (list = []) =>
  [...list].sort((a, b) => {
    const dateA = a?.visit_date || "";
    const dateB = b?.visit_date || "";
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA);
    }
    return Number(b?.id || 0) - Number(a?.id || 0);
  });

const buildAnthropometryPath = (patientId, { measurementId, mode, saved } = {}) => {
  const params = new URLSearchParams();
  if (saved) {
    params.set("saved", saved);
  }
  if (mode === "new") {
    params.set("mode", "new");
  }
  if (measurementId != null) {
    params.set("measurementId", String(measurementId));
  }
  const query = params.toString();
  return `/patients/${patientId}/anthropometry${query ? `?${query}` : ""}`;
};

const syncAnthropometryUrl = (patientId, { measurementId, mode } = {}) => {
  const nextPath = buildAnthropometryPath(patientId, { measurementId, mode });
  const current = `${window.location.pathname}${window.location.search}`;
  if (current !== nextPath) {
    window.history.replaceState({}, "", nextPath);
  }
};

const formatHistorySummary = (measurement) => {
  if (isDraftMeasurement(measurement)) {
    return "Rascunho";
  }

  const parts = [];
  const weight = measurement?.metabolic_data?.weight_kg;
  const bodyFat = measurement?.anthropometry?.body_fat_percentage;

  if (weight != null) {
    parts.push(
      `${Number(weight).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}kg`
    );
  }
  if (bodyFat != null) {
    parts.push(
      `${Number(bodyFat).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% de gordura`
    );
  }

  return parts.length > 0 ? parts.join(", ") : "Registro antropométrico";
};

/** Newest-first list: compare with older neighbor (index + 1). Down = weight drop (green), up = gain (pink). */
const getHistoryTrend = (measurement, olderMeasurement) => {
  const currentWeight = measurement?.metabolic_data?.weight_kg;
  const olderWeight = olderMeasurement?.metabolic_data?.weight_kg;

  if (currentWeight == null || olderWeight == null) {
    return "up";
  }

  if (Number(currentWeight) < Number(olderWeight)) {
    return "down";
  }

  return "up";
};

const getRegisterOrEditTitle = (hasData) => (hasData ? "Editar" : "Registrar dados");

const buildSectionEditPath = (patientId, section, measurement) => {
  const base =
    section === "circumference"
      ? `/patients/${patientId}/anthropometry/new`
      : `/patients/${patientId}/anthropometry/skinfolds`;

  if (measurement?.id == null) {
    return null;
  }

  const params = new URLSearchParams();
  params.set("mode", isDraftMeasurement(measurement) ? "new" : "edit");
  params.set("measurementId", String(measurement.id));
  return `${base}?${params.toString()}`;
};

const applyMeasurementSession = (measurement) => {
  if (isDraftMeasurement(measurement)) {
    return {
      mode: "new",
      measurementId: measurement.id,
      url: {
        mode: "new",
        measurementId: measurement.id,
      },
    };
  }

  return {
    mode: "view",
    measurementId: measurement.id,
    url: {
      measurementId: measurement.id,
    },
  };
};

const HistoryRecordItem = ({ measurement, trend, active, onSelect, onDelete }) => (
  <div
    className={`anthropometry-screen__history__item ${
      active ? "anthropometry-screen__history__item--active" : ""
    }`}
  >
    <button
      type="button"
      className="anthropometry-screen__history__item__select"
      onClick={() => onSelect(measurement)}
    >
      <img
        src={trend === "down" ? HistoryArrowDownIcon : HistoryArrowUpIcon}
        alt=""
        className="anthropometry-screen__history__item__trend"
      />
      <div className="anthropometry-screen__history__item__summary-row">
        <p className="anthropometry-screen__history__item__summary">
          {formatHistorySummary(measurement)}
        </p>
        {isDraftMeasurement(measurement) && (
          <StatusTag title="Rascunho" color="yellow" size="small" />
        )}
      </div>
      <p className="anthropometry-screen__history__item__date">
        {formatVisitDateLabel(measurement.visit_date)}
      </p>
      <span className="anthropometry-screen__history__item__arrow" aria-hidden="true">
        <img src={ChevronRightIcon} alt="" />
      </span>
    </button>
    <button
      type="button"
      className="anthropometry-screen__history__item__delete"
      aria-label={`Excluir registro de ${formatVisitDateLabel(measurement.visit_date)}`}
      onClick={(event) => {
        event.stopPropagation();
        onDelete(measurement);
      }}
    >
      <img src={DeleteIcon} alt="" />
    </button>
  </div>
);

const AnthropometryBreadcrumb = ({ patientId, patientName, mode, visitDateLabel }) => {
  if (mode === "new") {
    return (
      <div className="anthropometry-screen__breadcrumb">
        <a href="/patients" className="anthropometry-screen__breadcrumb__link">
          Pacientes
        </a>
        <div className="anthropometry-screen__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <a
          href={`/patients/${patientId}`}
          className="anthropometry-screen__breadcrumb__link"
        >
          {patientName || "Paciente"}
        </a>
        <div className="anthropometry-screen__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <a
          href={`/patients/${patientId}/anthropometry`}
          className="anthropometry-screen__breadcrumb__link"
        >
          Antropometria
        </a>
        <div className="anthropometry-screen__breadcrumb__divisor">
          <img src={ChevronRightIcon} alt="" />
        </div>
        <p className="anthropometry-screen__breadcrumb__current anthropometry-screen__breadcrumb__current--draft">
          Rascunho
        </p>
      </div>
    );
  }

  return (
    <div className="anthropometry-screen__breadcrumb">
      <a href="/patients" className="anthropometry-screen__breadcrumb__link">
        Pacientes
      </a>
      <div className="anthropometry-screen__breadcrumb__divisor">
        <img src={ChevronRightIcon} alt="" />
      </div>
      <a
        href={`/patients/${patientId}`}
        className="anthropometry-screen__breadcrumb__link"
      >
        {patientName || "Paciente"}
      </a>
      <div className="anthropometry-screen__breadcrumb__divisor">
        <img src={ChevronRightIcon} alt="" />
      </div>
      <a
        href={`/patients/${patientId}/anthropometry`}
        className="anthropometry-screen__breadcrumb__link"
      >
        Antropometria
      </a>
      {visitDateLabel ? (
        <>
          <div className="anthropometry-screen__breadcrumb__divisor">
            <img src={ChevronRightIcon} alt="" />
          </div>
          <p className="anthropometry-screen__breadcrumb__current">
            {visitDateLabel}
          </p>
        </>
      ) : null}
    </div>
  );
};

const CircunferenceItem = ({ title, fullTitle, value, unit }) => (
  <div
    className="anthropometry-screen__circunferences-content__item"
    title={fullTitle || title}
  >
    <p className="anthropometry-screen__circunferences-content__item__title">
      {title}
    </p>
    <p className="anthropometry-screen__circunferences-content__item__value">
      {value}
      {value !== "—" && unit ? ` ${unit}` : ""}
    </p>
  </div>
);

const AutomaticCalculationItem = ({ label, value }) => (
  <div
    className="anthropometry-screen__circunferences-content__item anthropometry-screen__auto-calculations__item"
    title={label}
  >
    <p className="anthropometry-screen__circunferences-content__item__title anthropometry-screen__auto-calculations__item__title">
      {label}
    </p>
    <p className="anthropometry-screen__circunferences-content__item__value">
      {value}
    </p>
  </div>
);

const SkinfoldsMenuItem = ({
  onNavigate,
  buttonTitle,
  showEmptyTag,
  emptyTagTitle = "Não preenchido",
  showAction = true,
}) => (
  <div className="anthropometry-screen__calculations__item">
    <div className="anthropometry-screen__calculations__item__info">
      <div className="anthropometry-screen__calculations__item__title-row">
        <p className="anthropometry-screen__calculations__item__title">
          Dobras Cutâneas
        </p>
        {showEmptyTag && (
          <StatusTag title={emptyTagTitle} color="primary" size="small" />
        )}
      </div>
      <p className="anthropometry-screen__calculations__item__description">
        Registro e acompanhamento de dobras cutâneas.
      </p>
    </div>
    {showAction && (
      <div className="anthropometry-screen__calculations__item__value-group">
        <DefaultButton
          title={buttonTitle}
          onClick={onNavigate}
          style="anthropometry-screen__calculations__action"
          size="small"
        />
      </div>
    )}
  </div>
);

const CircumferencesMenuItem = ({
  onNavigate,
  buttonTitle,
  showEmptyTag,
  emptyTagTitle = "Não preenchido",
  showAction = true,
}) => (
  <div className="anthropometry-screen__calculations__item">
    <div className="anthropometry-screen__calculations__item__info">
      <div className="anthropometry-screen__calculations__item__title-row">
        <p className="anthropometry-screen__calculations__item__title">
          Circunferências
        </p>
        {showEmptyTag && (
          <StatusTag title={emptyTagTitle} color="primary" size="small" />
        )}
      </div>
      <p className="anthropometry-screen__calculations__item__description">
        Registro e acompanhamento de circunferências.
      </p>
    </div>
    {showAction && (
      <div className="anthropometry-screen__calculations__item__value-group">
        <DefaultButton
          title={buttonTitle}
          onClick={onNavigate}
          style="anthropometry-screen__calculations__action"
          size="small"
        />
      </div>
    )}
  </div>
);

const PatientAnthropometry = () => {
  const { id: patientId } = useParams();
  const [searchParams] = useSearchParams();
  const draftMeasurementId = searchParams.get("measurementId");
  const [patient, setPatient] = useState();
  const [currentUser, setCurrentUser] = useState();
  const [measurements, setMeasurements] = useState([]);
  const [mode, setMode] = useState("view");
  const [selectedMeasurementId, setSelectedMeasurementId] = useState(null);
  const previousSelectionRef = useRef(null);
  const modeInitializedRef = useRef(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [measurementToDelete, setMeasurementToDelete] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [formError, setFormError] = useState("");
  const [notificationTitle, setNotificationTitle] = useState(
    "Registro de circunferências salvo com sucesso!"
  );
  const [showNotificationBar, setShowNotificationBar] = useState(false);
  const [pendingEditVersion, setPendingEditVersion] = useState(0);
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);

  const createDraftForPatient = useCallback(
    async (user, patientData) => {
      const visitDateForSave = toApiDate(todayDisplayDate());
      if (!visitDateForSave) {
        throw new Error("Invalid visit date");
      }

      return measurementService.createDraft(
        buildMinimalHistoryPayload({
          nutritionistId: user?.id,
          patientId,
          visitDate: visitDateForSave,
          patient: patientData,
        })
      );
    },
    [patientId]
  );

  const enterDraftSession = useCallback(
    (draft, nextMeasurements) => {
      const draftId = draft?.id != null ? Number(draft.id) : null;
      modeInitializedRef.current = true;
      setMode("new");
      setSelectedMeasurementId(draftId);
      setHistoryPage(1);
      if (nextMeasurements) {
        setMeasurements(nextMeasurements);
      }
      syncAnthropometryUrl(patientId, {
        mode: "new",
        measurementId: draftId != null ? String(draftId) : undefined,
      });
    },
    [patientId]
  );

  const hasHistory = measurements.length > 0;
  const latestMeasurement = measurements?.[0];

  const displayMeasurement = useMemo(() => {
    if (mode !== "view" || !hasHistory) {
      return null;
    }
    if (selectedMeasurementId != null) {
      return measurements.find((item) => item.id === selectedMeasurementId) ?? null;
    }
    return latestMeasurement ?? null;
  }, [mode, hasHistory, measurements, selectedMeasurementId, latestMeasurement]);

  const activeDraftId = useMemo(() => {
    if (mode !== "new") {
      return null;
    }
    if (selectedMeasurementId != null) {
      return selectedMeasurementId;
    }
    return draftMeasurementId ? Number(draftMeasurementId) : null;
  }, [mode, draftMeasurementId, selectedMeasurementId]);

  const draftMeasurement = useMemo(() => {
    if (mode !== "new" || activeDraftId == null) {
      return null;
    }
    return (
      measurements.find((item) => String(item.id) === String(activeDraftId)) || null
    );
  }, [mode, activeDraftId, measurements]);

  const sessionMeasurement = mode === "new" ? draftMeasurement : displayMeasurement;

  const previewMeasurement = useMemo(() => {
    if (!sessionMeasurement?.id) {
      return sessionMeasurement;
    }
    const pending = readPendingEdit(patientId, sessionMeasurement.id);
    return mergePendingIntoMeasurement(sessionMeasurement, pending);
  }, [sessionMeasurement, patientId, pendingEditVersion]);

  const circumferenceItems = useMemo(
    () => buildCircumferenceItems(previewMeasurement),
    [previewMeasurement]
  );

  const automaticCalculationItems = useMemo(
    () => buildAutomaticCalculationItems(previewMeasurement),
    [previewMeasurement]
  );

  const skinfoldHubItems = useMemo(
    () => buildSkinfoldHubItems(previewMeasurement, patient),
    [previewMeasurement, patient]
  );

  const circumferenceButtonTitle = getRegisterOrEditTitle(
    measurementHasCircumference(previewMeasurement)
  );

  const skinfoldsButtonTitle = getRegisterOrEditTitle(
    measurementHasSkinfolds(previewMeasurement)
  );

  const circumferencesShowEmptyTag = !measurementHasCircumference(previewMeasurement);
  const skinfoldsShowEmptyTag = !measurementHasSkinfolds(previewMeasurement);

  const isNoRecordHub = !hasHistory && mode === "view";
  const hubEmptyTagTitle = "Sem dados para mostrar";
  const hubShowSectionActions = !isNoRecordHub;
  const hubShowEmptyTag = (hasEmptySection) =>
    isNoRecordHub ? true : hasEmptySection;

  const canFinalizeDraft = useMemo(() => {
    if (!previewMeasurement || mode !== "new") {
      return false;
    }
    return (
      measurementHasCircumference(previewMeasurement) ||
      measurementHasSkinfolds(previewMeasurement)
    );
  }, [previewMeasurement, mode]);

  const pendingEdit = useMemo(() => {
    if (mode !== "view" || !displayMeasurement?.id) {
      return null;
    }
    return readPendingEdit(patientId, displayMeasurement.id);
  }, [mode, patientId, displayMeasurement?.id, pendingEditVersion]);

  const hasPendingEdits = useMemo(
    () =>
      mode === "view" &&
      displayMeasurement?.id != null &&
      hasPendingEdit(patientId, displayMeasurement.id),
    [mode, patientId, displayMeasurement?.id, pendingEditVersion]
  );

  const compositionChart = useMemo(
    () => buildCompositionProgressChartModel(measurements),
    [measurements]
  );

  const showProgressionChart = compositionChart.hasValues;

  const progressionChartMessage =
    "Ainda não há dados de gordura e massa magra para exibir o gráfico. Registre dobras cutâneas com protocolo selecionado para acompanhar a evolução.";

  const totalHistoryPages = Math.max(1, Math.ceil(measurements.length / HISTORY_PAGE_SIZE));

  const paginatedMeasurements = useMemo(() => {
    const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return measurements.slice(start, start + HISTORY_PAGE_SIZE);
  }, [measurements, historyPage]);

  const handleNavigateToSection = async (section) => {
    setFormError("");
    let targetMeasurement = sessionMeasurement;

    if (!targetMeasurement?.id) {
      setIsLoading(true);
      try {
        const user = currentUser || (await loginService.getCurrentUser());
        const patientData = patient || (await userService.getUserById(patientId));
        const draft = await createDraftForPatient(user, patientData);
        targetMeasurement = draft;
        if (!currentUser) {
          setCurrentUser(user);
        }
        if (!patient) {
          setPatient(patientData);
        }
      } catch (error) {
        console.error(error);
        setFormError("Não foi possível iniciar o novo registro. Tente novamente.");
        return;
      } finally {
        setIsLoading(false);
      }
    }

    const path = buildSectionEditPath(patientId, section, targetMeasurement);
    if (!path) {
      return;
    }

    if (section === "skinfolds" && targetMeasurement?.id != null && patientId) {
      sessionStorage.setItem(
        skinfoldEditMeasurementCacheKey(patientId, targetMeasurement.id),
        JSON.stringify(targetMeasurement)
      );
    }

    window.location.href = path;
  };

  const fetchData = useCallback(async ({ selectLatest = false } = {}) => {
    setIsLoading(true);
    try {
      const [userFound, patientFound, histories] = await Promise.all([
        loginService.getCurrentUser(),
        userService.getUserById(patientId),
        measurementService.listByPatient(patientId),
      ]);
      setCurrentUser(userFound);
      setPatient(patientFound);
      const nextMeasurements = sortMeasurementsNewestFirst(histories || []);
      setMeasurements(nextMeasurements);

      const searchParams = new URLSearchParams(window.location.search);
      const urlMeasurementId = searchParams.get("measurementId");
      const urlMode = searchParams.get("mode");
      const latestId = nextMeasurements[0]?.id ?? null;

      if (!modeInitializedRef.current) {
        if (nextMeasurements.length === 0) {
          setMode("view");
          setSelectedMeasurementId(null);
          syncAnthropometryUrl(patientId, {});
          modeInitializedRef.current = true;
        } else if (urlMode === "new") {
          const matchedDraft =
            urlMeasurementId &&
            nextMeasurements.find((item) => String(item.id) === String(urlMeasurementId));
          const draftId = matchedDraft
            ? matchedDraft.id
            : nextMeasurements.find(isDraftMeasurement)?.id ?? null;

          if (draftId != null) {
            setMode("new");
            setSelectedMeasurementId(Number(draftId));
            previousSelectionRef.current = latestId;
            syncAnthropometryUrl(patientId, {
              mode: "new",
              measurementId: String(draftId),
            });
          } else {
            setMode("view");
            setSelectedMeasurementId(null);
            syncAnthropometryUrl(patientId, {});
          }
        } else if (
          urlMeasurementId &&
          nextMeasurements.some((item) => String(item.id) === String(urlMeasurementId))
        ) {
          const matched = nextMeasurements.find(
            (item) => String(item.id) === String(urlMeasurementId)
          );
          const session = applyMeasurementSession(matched);
          setMode(session.mode);
          setSelectedMeasurementId(session.measurementId);
          syncAnthropometryUrl(patientId, session.url);
        } else {
          const latest = nextMeasurements[0];
          const session = applyMeasurementSession(latest);
          setMode(session.mode);
          setSelectedMeasurementId(session.measurementId);
          syncAnthropometryUrl(patientId, session.url);
        }
        modeInitializedRef.current = true;
        return;
      }

      if (selectLatest && latestId != null) {
        const latest = nextMeasurements[0];
        const session = applyMeasurementSession(latest);
        setMode(session.mode);
        setSelectedMeasurementId(session.measurementId);
        syncAnthropometryUrl(patientId, session.url);
        return;
      }

      if (nextMeasurements.length === 0) {
        setMode("view");
        setSelectedMeasurementId(null);
        syncAnthropometryUrl(patientId, {});
        modeInitializedRef.current = true;
        return;
      }

      if (urlMode === "new") {
        const matchedDraft =
          urlMeasurementId &&
          nextMeasurements.find((item) => String(item.id) === String(urlMeasurementId));
        const draftId = matchedDraft
          ? matchedDraft.id
          : nextMeasurements.find(isDraftMeasurement)?.id ?? null;

        if (draftId != null) {
          setMode("new");
          setSelectedMeasurementId(Number(draftId));
          syncAnthropometryUrl(patientId, {
            mode: "new",
            measurementId: String(draftId),
          });
        } else {
          setMode("view");
          setSelectedMeasurementId(null);
          syncAnthropometryUrl(patientId, {});
        }
        return;
      }

      if (
        urlMeasurementId &&
        nextMeasurements.some((item) => String(item.id) === String(urlMeasurementId))
      ) {
        const matched = nextMeasurements.find(
          (item) => String(item.id) === String(urlMeasurementId)
        );
        const session = applyMeasurementSession(matched);
        setMode(session.mode);
        setSelectedMeasurementId(session.measurementId);
        syncAnthropometryUrl(patientId, session.url);
        return;
      }

      setSelectedMeasurementId((currentId) => {
        if (currentId == null) {
          return currentId;
        }
        const stillExists = nextMeasurements.some((item) => item.id === currentId);
        const nextId = stillExists ? currentId : latestId;
        if (nextId != null) {
          syncAnthropometryUrl(patientId, { measurementId: nextId });
        }
        return nextId;
      });
    } catch (error) {
      console.error("Erro ao carregar antropometria", error);
      setMeasurements([]);
      if (!modeInitializedRef.current) {
        setMode("view");
        setSelectedMeasurementId(null);
        modeInitializedRef.current = true;
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, setIsLoading]);

  useEffect(() => {
    modeInitializedRef.current = false;
    previousSelectionRef.current = null;
    setHistoryPage(1);
  }, [patientId]);

  useEffect(() => {
    setHistoryPage((currentPage) => {
      const maxPage = Math.max(1, Math.ceil(measurements.length / HISTORY_PAGE_SIZE));
      return Math.min(currentPage, maxPage);
    });
  }, [measurements.length]);

  useEffect(() => {
    document.title = "Antropometria";
    setSelectedScreen("patients");
    fetchData();

    const searchParams = new URLSearchParams(window.location.search);
    const saved = searchParams.get("saved");
    if (saved === "1" || saved === "skinfolds" || saved === "session") {
      setNotificationTitle(
        saved === "skinfolds"
          ? "Registro de dobras cutâneas salvo com sucesso!"
          : saved === "session"
            ? "Novo registro salvo com sucesso!"
            : "Registro de circunferências salvo com sucesso!"
      );
      setShowNotificationBar(true);
      const measurementId = searchParams.get("measurementId");
      syncAnthropometryUrl(patientId, {
        measurementId: measurementId || undefined,
      });
    }
  }, [fetchData, patientId, setSelectedScreen]);

  const handleSelectHistoryItem = async (measurement) => {
    const isDraft = isDraftMeasurement(measurement);
    const nextMode = isDraft ? "new" : "view";

    if (mode === nextMode && measurement.id === selectedMeasurementId) {
      return;
    }

    setIsLoading(true);
    setFormError("");
    previousSelectionRef.current = measurement.id;
    setSelectedMeasurementId(measurement.id);
    setMode(nextMode);
    syncAnthropometryUrl(patientId, {
      mode: isDraft ? "new" : undefined,
      measurementId: measurement.id,
    });

    await new Promise((resolve) => setTimeout(resolve, 280));
    setIsLoading(false);
  };

  const handleAddNewRecord = async () => {
    setFormError("");
    previousSelectionRef.current = selectedMeasurementId;
    setIsLoading(true);

    try {
      const draft = await createDraftForPatient(currentUser, patient);
      enterDraftSession(draft, null);
      await fetchData();
    } catch (error) {
      console.error(error);
      setFormError("Não foi possível iniciar o novo registro. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalizeDraft = async () => {
    const draftId = activeDraftId;
    if (!draftId || !canFinalizeDraft) {
      return;
    }

    setIsLoading(true);
    setFormError("");

    try {
      const visitDateForSave =
        draftMeasurement?.visit_date || toApiDate(todayDisplayDate());
      const pending = readPendingEdit(patientId, draftId);
      const pendingPayload = pending
        ? buildSavePayloadFromPending({
            measurement: draftMeasurement || { id: draftId, visit_date: visitDateForSave },
            pending,
            nutritionistId: currentUser?.id,
            patientId,
          })
        : null;

      await measurementService.finalize(draftId, {
        nutritionist_id: currentUser?.id,
        patient_id: Number(patientId),
        visit_date: visitDateForSave,
        ...(pendingPayload || {}),
      });

      clearPendingEdit(patientId, draftId);
      setPendingEditVersion((version) => version + 1);
      modeInitializedRef.current = true;
      setMode("view");
      setSelectedMeasurementId(Number(draftId));
      syncAnthropometryUrl(patientId, { measurementId: String(draftId) });
      await fetchData();
      setNotificationTitle("Registro salvo com sucesso!");
      setShowNotificationBar(true);
    } catch (error) {
      console.error(error);
      setFormError("Não foi possível salvar o registro. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePendingEdit = async () => {
    if (!displayMeasurement?.id || !pendingEdit) {
      return;
    }

    const payload = buildSavePayloadFromPending({
      measurement: displayMeasurement,
      pending: pendingEdit,
      nutritionistId: currentUser?.id,
      patientId,
    });

    if (!payload) {
      return;
    }

    setIsLoading(true);
    setFormError("");

    try {
      await measurementService.update(displayMeasurement.id, payload);
      clearPendingEdit(patientId, displayMeasurement.id);
      setPendingEditVersion((version) => version + 1);
      await fetchData();
      setNotificationTitle("Registro salvo com sucesso!");
      setShowNotificationBar(true);
    } catch (error) {
      console.error(error);
      setFormError("Não foi possível salvar o registro. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscardPendingEdit = () => {
    if (!displayMeasurement?.id) {
      return;
    }
    clearPendingEdit(patientId, displayMeasurement.id);
    setPendingEditVersion((version) => version + 1);
    setFormError("");
    fetchData();
  };

  const handleDiscardDraft = async () => {
    const draftId = activeDraftId;
    if (!draftId) {
      return;
    }

    setIsLoading(true);
    setFormError("");

    try {
      await measurementService.discard(draftId);
      clearPendingEdit(patientId, draftId);
      setPendingEditVersion((version) => version + 1);

      const remaining = measurements.filter((item) => item.id !== draftId);
      setMeasurements(remaining);
      modeInitializedRef.current = true;

      if (remaining.length === 0) {
        setMode("view");
        setSelectedMeasurementId(null);
        syncAnthropometryUrl(patientId, {});
      } else {
        const restoreId =
          previousSelectionRef.current != null &&
          previousSelectionRef.current !== draftId &&
          remaining.some((item) => item.id === previousSelectionRef.current)
            ? previousSelectionRef.current
            : remaining[0].id;
        setMode("view");
        setSelectedMeasurementId(restoreId);
        syncAnthropometryUrl(patientId, { measurementId: restoreId });
      }

      await fetchData();
    } catch (error) {
      console.error(error);
      const message = "Não foi possível descartar o registro. Tente novamente.";
      setFormError(message);
      setNotificationTitle(message);
      setShowNotificationBar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (measurement) => {
    setMeasurementToDelete(measurement);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!measurementToDelete?.id) {
      return;
    }

    const deletedId = measurementToDelete.id;
    const wasSelected = selectedMeasurementId === deletedId;
    setShowDeleteConfirmModal(false);
    setMeasurementToDelete(null);
    setIsLoading(true);
    setFormError("");

    try {
      await measurementService.delete(deletedId);
      clearPendingEdit(patientId, deletedId);
      setPendingEditVersion((version) => version + 1);

      const remaining = measurements.filter((item) => item.id !== deletedId);
      setMeasurements(remaining);

      setHistoryPage((currentPage) => {
        const maxPage = Math.max(1, Math.ceil(remaining.length / HISTORY_PAGE_SIZE));
        return Math.min(currentPage, maxPage);
      });

      if (remaining.length === 0) {
        setMode("view");
        setSelectedMeasurementId(null);
        syncAnthropometryUrl(patientId, {});
        modeInitializedRef.current = true;
        await fetchData();
      } else if (wasSelected) {
        modeInitializedRef.current = true;
        await fetchData({ selectLatest: true });
      } else {
        await fetchData();
      }

      setNotificationTitle("Registro excluído com sucesso!");
      setShowNotificationBar(true);
    } catch (error) {
      console.error(error);
      const message = "Não foi possível excluir o registro. Tente novamente.";
      setFormError(message);
      setNotificationTitle(message);
      setShowNotificationBar(true);
    } finally {
      setIsLoading(false);
    }
  };
  const visitDateLabel = formatVisitDateLabel(sessionMeasurement?.visit_date);

  return (
    <>
    <div className="anthropometry-screen">
      <div className="anthropometry-screen__top">
        <AnthropometryBreadcrumb
          patientId={patientId}
          patientName={patient?.name}
          mode={mode}
          visitDateLabel={visitDateLabel}
        />
        {mode === "view" && !hasPendingEdits && (
          <DefaultButton
            title="Adicionar novo registro"
            onClick={handleAddNewRecord}
            style="anthropometry-screen__add-record"
            size="small"
          />
        )}
        {mode === "new" && (
          <DraftSessionActions
            className="anthropometry-screen__session-actions"
            onSave={handleFinalizeDraft}
            onDiscard={handleDiscardDraft}
            saveDisabled={!canFinalizeDraft}
          />
        )}
        {mode === "view" && hasPendingEdits && (
          <DraftSessionActions
            className="anthropometry-screen__session-actions"
            onSave={handleSavePendingEdit}
            onDiscard={handleDiscardPendingEdit}
            discardModalTitle="Descartar alterações"
            discardModalMessage="As alterações pendentes serão descartadas. Deseja continuar?"
            discardConfirmLabel="Descartar alterações"
          />
        )}
      </div>

      <div className="anthropometry-screen__content">
        <div className="anthropometry-screen__left">
          <div className="anthropometry-screen__calculations anthropometry-screen__calculations--hub">
            <div className="anthropometry-screen__calculations__header">
              <h2>Antropometria</h2>
            </div>
            <div className="anthropometry-screen__hub-section">
              <CircumferencesMenuItem
                onNavigate={() => handleNavigateToSection("circumference")}
                buttonTitle={circumferenceButtonTitle}
                showEmptyTag={hubShowEmptyTag(circumferencesShowEmptyTag)}
                emptyTagTitle={isNoRecordHub ? hubEmptyTagTitle : "Não preenchido"}
                showAction={hubShowSectionActions}
              />
              {circumferenceItems.length > 0 && (
                <div className="anthropometry-screen__circunferences-content anthropometry-screen__circunferences-content--inline">
                  {circumferenceItems.map((item) => (
                    <CircunferenceItem
                      key={item.key}
                      title={item.title}
                      fullTitle={item.fullTitle}
                      value={item.value}
                      unit={item.unit}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="anthropometry-screen__hub-section">
              <SkinfoldsMenuItem
                onNavigate={() => handleNavigateToSection("skinfolds")}
                buttonTitle={skinfoldsButtonTitle}
                showEmptyTag={hubShowEmptyTag(skinfoldsShowEmptyTag)}
                emptyTagTitle={isNoRecordHub ? hubEmptyTagTitle : "Não preenchido"}
                showAction={hubShowSectionActions}
              />
              {skinfoldHubItems.sites.length > 0 && (
                <div className="anthropometry-screen__circunferences-content anthropometry-screen__circunferences-content--inline anthropometry-screen__skinfolds-hub__sites">
                  {skinfoldHubItems.sites.map((item) => (
                    <CircunferenceItem
                      key={item.key}
                      title={item.title}
                      fullTitle={item.fullTitle}
                      value={item.value}
                    />
                  ))}
                </div>
              )}
              {skinfoldHubItems.protocols.length > 0 && (
                <div className="anthropometry-screen__circunferences-content anthropometry-screen__circunferences-content--inline anthropometry-screen__skinfolds-hub__protocols">
                  {skinfoldHubItems.protocols.map((item) => (
                    <AutomaticCalculationItem
                      key={item.key}
                      label={item.title}
                      value={item.value}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="anthropometry-screen__auto-calculations">
            <div className="anthropometry-screen__auto-calculations__header">
              <h2>Cálculos Automáticos</h2>
            </div>
            <div className="anthropometry-screen__auto-calculations__grid">
              {automaticCalculationItems.map((item) => (
                <AutomaticCalculationItem
                  key={item.key}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="anthropometry-screen__right">
          {showProgressionChart ? (
            <div className="anthropometry-screen__chart">
              <div className="anthropometry-screen__chart__header anthropometry-screen__chart__header--with-action">
                <div>
                  <h2>Gráfico de Progressão</h2>
                  <p className="anthropometry-screen__chart__subtitle">
                    Evolução da Gordura e Massa Magra
                  </p>
                </div>
                <DefaultButton
                  title="Ver progresso detalhado"
                  type="secondary"
                  onClick={() => {
                    window.location.href = `/patients/${patientId}/anthropometry/progress`;
                  }}
                  style="anthropometry-screen__chart__action"
                  size="small"
                />
              </div>
              <div className="anthropometry-screen__chart__canvas">
                <Line
                  data={compositionChart.chartData}
                  options={compositionChart.chartOptions}
                />
              </div>
            </div>
          ) : (
            <div className="anthropometry-screen__chart anthropometry-screen__chart--empty">
              <div className="anthropometry-screen__chart__header">
                <h2>Gráfico de Progressão</h2>
              </div>
              <div className="anthropometry-screen__chart__empty">
                <p className="anthropometry-screen__chart__subtitle">
                  Evolução da Gordura e Massa Magra
                </p>
                <p className="anthropometry-screen__chart__empty-message">
                  {progressionChartMessage}
                </p>
              </div>
            </div>
          )}

          <div className="anthropometry-screen__history">
            <div className="anthropometry-screen__history__header">
              <h2>Histórico de Registros</h2>
            </div>
            {measurements.length > 0 ? (
              <>
                <div className="anthropometry-screen__history__list">
                  {paginatedMeasurements.map((measurement) => {
                    const fullIndex = measurements.findIndex((item) => item.id === measurement.id);
                    return (
                      <HistoryRecordItem
                        key={measurement.id}
                        measurement={measurement}
                        trend={getHistoryTrend(measurement, measurements[fullIndex + 1])}
                        active={
                          (mode === "view" &&
                            measurement.id === displayMeasurement?.id) ||
                          (mode === "new" &&
                            String(measurement.id) === String(activeDraftId))
                        }
                        onSelect={handleSelectHistoryItem}
                        onDelete={handleOpenDeleteConfirm}
                      />
                    );
                  })}
                </div>
                {totalHistoryPages > 1 && (
                  <div className="anthropometry-screen__history__pagination">
                    <NumericPagination
                      currentPage={historyPage}
                      totalPages={totalHistoryPages}
                      onSelectPage={(page) => setHistoryPage(page + 1)}
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="anthropometry-screen__empty">Nenhum registro ainda</p>
            )}
          </div>
        </div>
      </div>

      {formError && (
        <p className="anthropometry-screen__error">{formError}</p>
      )}
    </div>

    <DefaultModal
      title="Excluir registro"
      showModal={showDeleteConfirmModal}
      onClose={() => {
        setShowDeleteConfirmModal(false);
        setMeasurementToDelete(null);
      }}
    >
      <div className="anthropometry-screen__confirm">
        <p>
          Confirmar exclusão do registro de{" "}
          {formatVisitDateLabel(measurementToDelete?.visit_date)}?
        </p>
        <div className="anthropometry-screen__confirm__actions">
          <DefaultButton
            title="Voltar"
            type="secondary"
            onClick={() => {
              setShowDeleteConfirmModal(false);
              setMeasurementToDelete(null);
            }}
          />
          <DefaultButton
            title="Excluir"
            type="primary"
            onClick={handleConfirmDelete}
          />
        </div>
      </div>
    </DefaultModal>

    <NotificationBar
      title={notificationTitle}
      durationInSeconds={5}
      show={showNotificationBar}
      setShow={setShowNotificationBar}
    />
    </>
  );
};

export default PatientAnthropometry;
